import { useEffect, useState } from "react";
import type { StoreApi } from "zustand";
import { useTranslation } from "react-i18next";
import {
  useAuthStore,
  useLanguageStore,
  type AuthController,
  type AuthState,
  type FeatureNavNode,
} from "@nasam/core";
import {
  cn,
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LoginLinearIcon,
  LogoutLinearIcon,
} from "@nasam/ui";
import { authController as defaultAuthController } from "../auth";
import { LogoutConfirmDialog } from "./LogoutConfirmDialog";
import { resolveIcon } from "./iconRegistry";
import { navNodeTitle } from "./navNodeTitle";

export interface UserMenuProps {
  readonly expanded?: boolean;
  readonly authController?: AuthController | undefined;
  readonly authStore?: StoreApi<AuthState> | undefined;
  /**
   * The registry's `account` node (the profile screens), when one is
   * registered. On the rail the avatar then opens it directly instead of the
   * dropdown; in the mobile menu its tabs are listed in the dropdown.
   */
  readonly accountNode?: FeatureNavNode | undefined;
  readonly accountActive?: boolean;
  readonly onSelectAccountNode?: ((node: FeatureNavNode) => void) | undefined;
}

function getInitials(name?: string | null, fallback = "U"): string {
  if (!name || name.trim() === "") {
    return fallback;
  }
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0]?.slice(0, 2).toUpperCase() ?? fallback;
  }
  const first = parts[0]?.[0] ?? "";
  const second = parts[parts.length - 1]?.[0] ?? "";
  return `${first}${second}`.toUpperCase();
}

export function UserMenu({
  expanded = false,
  authController = defaultAuthController,
  authStore = useAuthStore,
  accountNode,
  accountActive = false,
  onSelectAccountNode,
}: UserMenuProps) {
  const { t } = useTranslation();
  const currentLanguage = useLanguageStore((state) => state.language);
  const [user, setUser] = useState(() => authStore.getState().user);

  useEffect(() => {
    return authStore.subscribe((state) => {
      setUser(state.user);
    });
  }, [authStore]);

  const displayName =
    currentLanguage === "ar"
      ? (user?.nameAr ?? user?.nameEn ?? user?.username ?? "User")
      : (user?.nameEn ?? user?.nameAr ?? user?.username ?? "User");

  const emailOrUsername = user?.email ?? user?.username ?? "";
  const initials = getInitials(displayName);
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  // Same swap as the rail's own logout button and LogoutConfirmDialog (see
  // NavRail) -- a CSS mirror had no visible effect on this glyph.
  const LogoutIcon =
    currentLanguage === "ar" ? LogoutLinearIcon : LoginLinearIcon;

  const avatar = (
    <Avatar className={expanded ? "size-10 shrink-0" : "size-9"}>
      {user?.avatarUrl ? (
        <AvatarImage src={user.avatarUrl} alt="" className="object-cover" />
      ) : null}
      <AvatarFallback className="text-body-s-b">{initials}</AvatarFallback>
    </Avatar>
  );

  // Rail: the avatar is the account node's own trigger, like a rail icon.
  if (!expanded && accountNode !== undefined) {
    return (
      <button
        type="button"
        data-testid="rail-account-button"
        data-active={accountActive ? "true" : "false"}
        aria-label={displayName}
        aria-current={accountActive ? "page" : undefined}
        onClick={() => onSelectAccountNode?.(accountNode)}
        className={cn(
          "flex size-10 items-center justify-center rounded-full p-0 transition-shadow duration-150 ease-emphasized focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-white",
          accountActive
            ? "ring-2 ring-neutral-white"
            : "hover:ring-2 hover:ring-white/40",
        )}
      >
        {avatar}
      </button>
    );
  }

  const accountTabs = accountNode?.children ?? [];

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="user-menu-trigger"
            aria-label={displayName}
            className={cn(
              "flex items-center transition-shadow duration-150 ease-emphasized hover:ring-2 hover:ring-primary/20 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
              expanded
                ? "h-full w-full justify-end gap-mobile-profile px-2 text-end"
                : "size-10 justify-center rounded-full p-0",
            )}
          >
            {expanded && (
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-body-s-r text-purple-900">
                  {displayName}
                </span>
                <span className="truncate text-caption-s text-tab-normal-text">
                  {user?.email ?? ""}
                </span>
              </span>
            )}
            {avatar}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side={expanded ? "top" : "right"}
          align="end"
          className="w-56 p-1"
        >
          <DropdownMenuLabel className="px-3 py-2">
            <p className="text-body-s-b text-text truncate">{displayName}</p>
            {emailOrUsername !== "" && (
              <p className="text-caption-s text-text-placeholder truncate">
                {emailOrUsername}
              </p>
            )}
          </DropdownMenuLabel>
          {/* The mobile menu has no drawer, so the profile tabs live here. */}
          {expanded && accountTabs.length > 0 && (
            <>
              <DropdownMenuSeparator />
              {accountTabs.map((tab) => {
                const Icon = resolveIcon(tab.icon);
                return (
                  <DropdownMenuItem
                    key={tab.id}
                    data-testid={`user-menu-${tab.id}`}
                    onClick={() => onSelectAccountNode?.(tab)}
                    className="cursor-pointer"
                  >
                    <Icon className="size-4 me-2" />
                    <span>{navNodeTitle(tab, t, user)}</span>
                  </DropdownMenuItem>
                );
              })}
            </>
          )}
          {/* Desktop has its own standalone logout button in the rail now
              (see NavRail) -- only the mobile drawer, which has no rail,
              still needs this as its one path to logging out. */}
          {expanded && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="logout-button"
                destructive
                onClick={() => setConfirmingLogout(true)}
                className="cursor-pointer"
              >
                <LogoutIcon className="size-4 me-2" />
                <span>{t("common:logout")}</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {expanded && (
        <LogoutConfirmDialog
          open={confirmingLogout}
          onOpenChange={setConfirmingLogout}
          authController={authController}
        />
      )}
    </>
  );
}
